import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';

function AdminResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentTenant, getCompanyName } = useConfig();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const companyName = currentTenant?.companyName || getCompanyName();

  useEffect(() => {
    if (!token || !email) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token, email]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/[A-Z]/.test(formData.password) || !/[a-z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      setError('Password must include uppercase, lowercase, and a number');
      return;
    }

    setLoading(true);

    try {
      await api.post('/reset-password', {
        token,
        email,
        newPassword: formData.password,
        userType: 'admin'
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-box">
          <div className="admin-access-badge" style={{ background: 'var(--color-success)', color: 'white' }}>
            ✓ PASSWORD UPDATED
          </div>
          <h2 className="admin-login-title">Password Reset Successful!</h2>
          <p className="admin-login-subtitle">
            Your admin password has been updated. You can now log in with your new password.
          </p>
          <button
            onClick={() => navigate('/admin/login')}
            className="admin-login-submit"
            style={{ marginTop: '20px' }}
          >
            Go to Admin Login
          </button>
        </div>
      </div>
    );
  }

  if (!token || !email) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-box">
          <div className="admin-access-badge" style={{ background: 'var(--color-error)', color: 'white' }}>
            ! INVALID LINK
          </div>
          <h2 className="admin-login-title">Invalid Reset Link</h2>
          <p className="admin-login-subtitle">
            This password reset link is invalid or has expired.
          </p>
          <button
            onClick={() => navigate('/admin/forgot-password')}
            className="admin-login-submit"
            style={{ marginTop: '20px' }}
          >
            Request New Reset Link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login-container">
      <div className="admin-login-box">
        <div className="admin-access-badge">
          🔐 SET NEW PASSWORD
        </div>

        <h2 className="admin-login-title">
          {currentTenant ? `${companyName} Admin` : 'Reset Admin Password'}
        </h2>

        <p className="admin-login-subtitle">
          Enter your new admin password below.
        </p>

        {error && (
          <div className="admin-login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label className="admin-form-label">
              New Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
              className="admin-form-input"
              placeholder="At least 8 characters"
              disabled={loading}
            />
            <p style={{ fontSize: '12px', color: 'var(--color-text-light)', margin: '5px 0 0 0' }}>
              Must include uppercase, lowercase, and a number
            </p>
          </div>

          <div className="admin-form-group-last">
            <label className="admin-form-label">
              Confirm New Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="admin-form-input"
              placeholder="Re-enter your password"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="admin-login-submit"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="admin-login-footer">
          <button
            onClick={() => navigate('/admin/login')}
            className="admin-back-button"
          >
            ← Back to Admin Login
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminResetPassword;
